/* global google */

import styled from '@emotion/styled'
import { StyledHTMLElement } from 'shop/theme/types'
import React, { useEffect, useState, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { GeocodedAddress } from './utils'
import { useShop } from 'shop/hooks'
import { trackGA4CustomSearchByAddressSuggestion } from 'tracker/GA/custom'
import usePlacesAutocomplete from 'use-places-autocomplete'

type AddressSuggestionsProps = {
  inputValue: string
  onSelect: ({
    newAddress,
    geocodedAddress
  }: {
    newAddress?: string
    geocodedAddress?: GeocodedAddress
  }) => void
  setHasUserChangedInput: React.Dispatch<React.SetStateAction<boolean>>
}

export const isAddressEligible = (inputValue: string) => {
  const strippedAddressLength = inputValue.replace(/\s/g, '')
  return strippedAddressLength.length >= 3
}

const AddressSuggestions = ({
  inputValue,
  onSelect,
  setHasUserChangedInput
}: AddressSuggestionsProps) => {
  const { setIsSortedPartnerStoresLoading } = useShop()
  const sessionToken = useRef(uuidv4())
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const searchOptions = {
    location: new google.maps.LatLng({ lat: 51.5137137, lng: -0.1285204 }),
    radius: 30,
    sessiontoken: sessionToken.current
  }

  const {
    suggestions: { data: suggestions },
    clearSuggestions,
    setValue
  } = usePlacesAutocomplete({
    requestOptions: searchOptions,
    debounce: 500,
    defaultValue: inputValue
  })

  useEffect(() => {
    if (isAddressEligible(inputValue)) {
      setValue(inputValue)
    }
    if (!isAddressEligible) clearSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  const handleAddressSuggestionSelect = (address: string) => {
    trackGA4CustomSearchByAddressSuggestion('slerpGA4Tracker')
    trackGA4CustomSearchByAddressSuggestion('merchantGA4Tracker')
    setIsSortedPartnerStoresLoading(true)
    onSelect({ newAddress: address })
    // stop fetching suggestions after selecting an address
    setHasUserChangedInput(false)
  }

  return (
    <AutoCompleteInputGroup data-testid='address-autocomplete'>
      <div className='autocomplete-dropdown'>
        {suggestions.map((suggestion, i) => {
          const isActive = i === activeIndex
          const className = isActive
            ? 'suggestion-item suggestion-item-active'
            : 'suggestion-item'

          return (
            <div
              data-testid={`addressSuggestion`}
              className={className}
              key={`address-suggestion-${i}`}
              onClick={() =>
                handleAddressSuggestionSelect(suggestion.description)
              }
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <span>{suggestion.description}</span>
            </div>
          )
        })}
      </div>
    </AutoCompleteInputGroup>
  )
}
const AutoCompleteInputGroup = styled.div<StyledHTMLElement>(({ theme }) => ({
  position: 'relative',
  '.autocomplete-dropdown': {
    fontSize: theme.fontSizes[1],
    color: theme.colors.textBody,
    width: '100%',
    zIndex: 10
  },
  '.suggestion-item': {
    background: '#ffffff',
    cursor: 'pointer',
    padding: '16px 16px',
    lineHeight: '16px',
    marginBottom: '4px',
    color: theme.colors.body,

    [theme.mediaQueries.viewport7]: {
      padding: '16px 12px'
    }
  },
  '.suggestion-item:last-child': {
    marginBottom: '0'
  },
  '.suggestion-item-active': {
    background: '#FAFAFA',
    borderRadius: '0',

    [theme.mediaQueries.viewport7]: {
      borderRadius: theme.borderRadius
    }
  }
}))

export default AddressSuggestions
